import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import {
  LayoutDashboard, ClipboardList, Users, Boxes, FlaskConical, MessageSquarePlus, Ticket, Settings, LogOut, Menu, Percent, Share2, Truck,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";

const links = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/orders", label: "Orders", icon: ClipboardList },
  { to: "/admin/clients", label: "Clients", icon: Users },
  { to: "/admin/inventory", label: "Inventory", icon: Boxes },
  { to: "/admin/reconstitution", label: "Reconstitution", icon: FlaskConical },
  { to: "/admin/requests", label: "Peptide Requests", icon: MessageSquarePlus },
  { to: "/admin/invites", label: "Invite Codes", icon: Ticket },
  { to: "/admin/promos", label: "Discount Codes", icon: Percent },
  { to: "/admin/referrals", label: "Referrals", icon: Share2 },
  { to: "/admin/vendor-orders", label: "Vendor Orders", icon: Truck },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

function NavItems({ onNavigate }) {
  const { logout } = useAuth();
  return (
    <div className="flex h-full flex-col">
      <div className="px-5 py-5"><Logo to="/admin" /></div>
      <p className="eyebrow px-5 pb-2 pt-2">Owner CRM</p>
      <nav className="flex-1 space-y-0.5 px-3">
        {links.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={onNavigate}
            data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                isActive ? "bg-emerald-500/10 text-emerald-300" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`
            }
          >
            <Icon className="h-4 w-4" />
            {label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-border p-3">
        <Button variant="ghost" className="w-full justify-start gap-3 text-muted-foreground" onClick={logout} data-testid="admin-logout-button">
          <LogOut className="h-4 w-4" /> Log out
        </Button>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen lg:pl-64">
      <aside className="fixed inset-y-0 left-0 hidden w-64 border-r border-border bg-[#0A141C] lg:block">
        <NavItems />
      </aside>
      <header className="sticky top-0 z-40 flex h-14 items-center gap-3 border-b border-border/60 bg-background/80 px-4 backdrop-blur-xl lg:hidden">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="admin-mobile-menu-button"><Menu className="h-5 w-5" /></Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-64 bg-[#0A141C] p-0">
            <NavItems onNavigate={() => setOpen(false)} />
          </SheetContent>
        </Sheet>
        <Logo to="/admin" small />
      </header>
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-8 lg:py-10">
        <Outlet />
      </main>
    </div>
  );
}

export const PageHeader = ({ eyebrow, title, children }) => (
  <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between rise">
    <div>
      {eyebrow && <p className="eyebrow mb-2">{eyebrow}</p>}
      <h1 className="text-3xl font-semibold sm:text-4xl" data-testid="page-title">{title}</h1>
    </div>
    {children && <div className="flex flex-wrap gap-2">{children}</div>}
  </div>
);
